import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useCart } from '../contexts/CartContext';
import {
  readMemberFromStorage,
  clearMemberSession,
  JAVILAND_MEMBER_AUTH_EVENT,
  getMemberFirstName
} from '../utils/memberSession';
import { HOLDING_STORE_MENU, HOLDING_BRAND_NAMES } from '../constants/holdingBrands';
import MemberAccountMenu from './MemberAccountMenu';
import './MemberAccountMenu.css';
import './Header.css';

const MOBILE_HEADER_BREAKPOINT = 768;

const Header = ({ onCommunityClick, onEcommerceClick, brandVariant = 'javiland' }) => {
  const { t } = useTranslation();
  const { itemCount, toggleCart } = useCart();
  const isCostal = brandVariant === 'costal';
  const [currentPath, setCurrentPath] = useState(() => window.location.pathname.replace(/\/$/, '') || '/');
  const [memberUser, setMemberUser] = useState(() => readMemberFromStorage());
  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [showProyectosMenu, setShowProyectosMenu] = useState(false);
  const [showServiciosMenu, setShowServiciosMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMobileProyectos, setShowMobileProyectos] = useState(false);
  const [showMobileServicios, setShowMobileServicios] = useState(false);
  const hideTimeoutRef = useRef(null);
  const menuTimeoutRef = useRef(null);

  useEffect(() => {
    const isNarrow = () => window.innerWidth <= MOBILE_HEADER_BREAKPOINT;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (isNarrow()) {
        setIsVisible(true);
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
      } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
        setIsVisible(false);
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
        }
      } else if (currentScrollY < lastScrollY) {
        setIsVisible(true);
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
        }
        hideTimeoutRef.current = setTimeout(() => {
          setIsVisible(false);
        }, 2000);
      }

      if (currentScrollY > 100) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }

      setLastScrollY(currentScrollY);
    };

    const handleResize = () => {
      if (isNarrow()) {
        setIsVisible(true);
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = null;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, [lastScrollY]);

  useEffect(() => {
    const syncMember = () => setMemberUser(readMemberFromStorage());
    const syncPath = () => setCurrentPath(window.location.pathname.replace(/\/$/, '') || '/');
    syncMember();
    syncPath();
    window.addEventListener('popstate', syncMember);
    window.addEventListener('popstate', syncPath);
    window.addEventListener(JAVILAND_MEMBER_AUTH_EVENT, syncMember);
    window.addEventListener('storage', syncMember);
    return () => {
      window.removeEventListener('popstate', syncMember);
      window.removeEventListener('popstate', syncPath);
      window.removeEventListener(JAVILAND_MEMBER_AUTH_EVENT, syncMember);
      window.removeEventListener('storage', syncMember);
    };
  }, []);

  const memberFirstName = memberUser ? getMemberFirstName(memberUser) : '';
  const memberShortLabel =
    memberFirstName ||
    (memberUser?.email ? String(memberUser.email).split('@')[0] : '') ||
    '';

  const handleMemberLogout = () => {
    clearMemberSession();
    setMemberUser(null);
    setShowMobileMenu(false);
    window.history.pushState({}, '', isCostal ? '/costal' : '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const navigateToPage = (path) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };

  const goLogin = () => {
    if (onCommunityClick) onCommunityClick();
    else navigateToPage('/comunidad/ingresar');
  };

  const isNavActive = (path) => {
    if (path === '/tienda') return currentPath === '/tienda' || currentPath.startsWith('/producto/');
    if (path === '/comunidad') return currentPath === '/comunidad';
    return currentPath === path;
  };

  const goBrandHub = (brand) => {
    const path = brand.menuPath || brand.hubPath;
    if (!path) return;
    if (path.startsWith('http')) {
      window.open(path, '_blank', 'noopener,noreferrer');
      return;
    }
    navigateToPage(path);
  };

  return (
    <>
    <header className={`header ${isScrolled ? 'scrolled' : ''} ${!isVisible ? 'hidden' : ''}${isCostal ? ' header--costal' : ''}`}>
      <div className="header-content">
        <div className="logo-container" onClick={() => navigateToPage(isCostal ? '/costal' : '/')}>
          {isCostal ? (
            <span className="logo-costal-lockup" aria-label="Costal Campesino">
              <img
                src="/images/brand/costal-icon.svg"
                alt=""
                className="logo-costal-icon"
              />
              <span className="logo-costal-wordmark">
                <span className="logo-costal-name">Costal</span>
                <span className="logo-costal-sub">Campesino</span>
              </span>
            </span>
          ) : (
            <>
              <img
                src="/images/Logo_Javiland_Largo.png"
                alt="Javiland Logo"
                className="logo-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <span
                className="logo-text fallback"
                style={{ display: 'none' }}
              >
                JAVILAND
              </span>
            </>
          )}
        </div>
        
        <button 
          className="mobile-menu-toggle"
          onClick={() => {
            setShowMobileMenu(!showMobileMenu);
            setShowMobileProyectos(false);
            setShowMobileServicios(false);
          }}
          aria-label="Toggle menu"
        >
          <i className={`fas ${showMobileMenu ? 'fa-times' : 'fa-bars'}`}></i>
        </button>
        
        <nav className="nav-menu-container">
          <ul className="nav-menu">
            {isCostal ? (
              <>
                <li>
                  <button
                    className={`nav-link${isNavActive('/costal') ? ' nav-link--active' : ''}`}
                    onClick={() => navigateToPage('/costal')}
                  >
                    Home
                  </button>
                </li>
                <li>
                  <button
                    className={`nav-link${isNavActive('/tienda') ? ' nav-link--active' : ''}`}
                    onClick={() => navigateToPage('/tienda')}
                  >
                    Tienda
                  </button>
                </li>
                <li>
                  <button
                    className={`nav-link${isNavActive('/comunidad') ? ' nav-link--active' : ''}`}
                    onClick={() => navigateToPage('/comunidad')}
                  >
                    Comunidad
                  </button>
                </li>
              </>
            ) : (
              <>
            <li><button className="nav-link" onClick={() => navigateToPage('/')}>{t('header.home')}</button></li>
            
            <li 
              className="nav-item-dropdown"
              onMouseEnter={() => {
                if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
                setShowProyectosMenu(true);
                setShowServiciosMenu(false);
              }}
              onMouseLeave={() => {
                menuTimeoutRef.current = setTimeout(() => setShowProyectosMenu(false), 200);
              }}
            >
              <button className="nav-link" onClick={() => navigateToPage('/proyectos')}>
                Proyectos Digitales <i className="fas fa-chevron-down"></i>
              </button>
              {showProyectosMenu && (
                <div className="dropdown-menu">
                  {HOLDING_STORE_MENU.map((brand) => (
                    <button
                      key={brand.id}
                      className="dropdown-item"
                      onClick={() => {
                        goBrandHub(brand);
                        setShowProyectosMenu(false);
                      }}
                    >
                      {brand.menuLabel || brand.name}
                      {brand.status === 'proximamente' ? ' · pronto' : ''}
                    </button>
                  ))}
                </div>
              )}
            </li>

            <li
              className="nav-item-dropdown"
              onMouseEnter={() => {
                if (menuTimeoutRef.current) clearTimeout(menuTimeoutRef.current);
                setShowServiciosMenu(true);
                setShowProyectosMenu(false);
              }}
              onMouseLeave={() => {
                menuTimeoutRef.current = setTimeout(() => setShowServiciosMenu(false), 200);
              }}
            >
              <button
                className="nav-link"
                onClick={() => navigateToPage(HOLDING_BRAND_NAMES.productStudio.hubPath)}
              >
                Servicios <i className="fas fa-chevron-down"></i>
              </button>
              {showServiciosMenu && (
                <div className="dropdown-menu">
                  <button
                    className="dropdown-item"
                    onClick={() => {
                      navigateToPage(HOLDING_BRAND_NAMES.productStudio.hubPath);
                      setShowServiciosMenu(false);
                    }}
                  >
                    {HOLDING_BRAND_NAMES.productStudio.name}
                  </button>
                </div>
              )}
            </li>

            <li>
              <button className="nav-link" onClick={() => navigateToPage('/comunidad')}>
                {t('header.community')}
              </button>
            </li>
              </>
            )}
          </ul>
        </nav>
        
        {showMobileMenu && (
          <nav className="mobile-menu">
            {memberUser && (
              <div className="mobile-member-banner">
                <button
                  type="button"
                  className="mobile-nav-link mobile-member-to-perfil-btn"
                  onClick={() => { navigateToPage('/perfil'); setShowMobileMenu(false); }}
                  title={memberUser.email || ''}
                >
                  {memberUser.avatar_data ? (
                    <img src={memberUser.avatar_data} alt="" className="mobile-member-btn-avatar" />
                  ) : (
                    <span className="mobile-member-btn-avatar mobile-member-btn-avatar--placeholder" aria-hidden>
                      <i className="fas fa-user" />
                    </span>
                  )}
                  <span className="mobile-member-btn-name">{memberShortLabel}</span>
                </button>
                <button type="button" className="mobile-nav-link mobile-member-logout" onClick={handleMemberLogout}>
                  {t('header.memberLogout')}
                </button>
              </div>
            )}
            <button className="mobile-nav-link" onClick={() => { navigateToPage(isCostal ? '/costal' : '/'); setShowMobileMenu(false); }}>
              {isCostal ? 'Home' : t('header.home')}
            </button>
            {isCostal ? (
              <>
              <button className="mobile-nav-link" onClick={() => { navigateToPage('/tienda'); setShowMobileMenu(false); }}>
                Tienda
              </button>
              <button className="mobile-nav-link" onClick={() => { navigateToPage('/comunidad'); setShowMobileMenu(false); }}>
                Comunidad
              </button>
              </>
            ) : (
            <>
            <div className="mobile-nav-group">
              <button
                type="button"
                className="mobile-nav-link mobile-nav-expandable"
                onClick={() => setShowMobileProyectos(!showMobileProyectos)}
                aria-expanded={showMobileProyectos}
              >
                Proyectos Digitales{' '}
                <i className={`fas fa-chevron-down mobile-nav-chevron ${showMobileProyectos ? 'open' : ''}`} />
              </button>
              {showMobileProyectos && (
                <div className="mobile-nav-sublinks">
                  {HOLDING_STORE_MENU.map((brand) => (
                    <button
                      key={brand.id}
                      type="button"
                      className="mobile-nav-sublink"
                      onClick={() => {
                        goBrandHub(brand);
                        setShowMobileMenu(false);
                        setShowMobileProyectos(false);
                      }}
                    >
                      {brand.menuLabel || brand.name}
                      {brand.status === 'proximamente' ? ' · pronto' : ''}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="mobile-nav-group">
              <button
                type="button"
                className="mobile-nav-link mobile-nav-expandable"
                onClick={() => setShowMobileServicios(!showMobileServicios)}
                aria-expanded={showMobileServicios}
              >
                Servicios{' '}
                <i className={`fas fa-chevron-down mobile-nav-chevron ${showMobileServicios ? 'open' : ''}`} />
              </button>
              {showMobileServicios && (
                <div className="mobile-nav-sublinks">
                  <button
                    type="button"
                    className="mobile-nav-sublink"
                    onClick={() => {
                      navigateToPage(HOLDING_BRAND_NAMES.productStudio.hubPath);
                      setShowMobileMenu(false);
                      setShowMobileServicios(false);
                    }}
                  >
                    {HOLDING_BRAND_NAMES.productStudio.name}
                  </button>
                </div>
              )}
            </div>
            <button className="mobile-nav-link" onClick={() => { navigateToPage('/comunidad'); setShowMobileMenu(false); }}>
              {t('header.community')}
            </button>
            </>
            )}
            {!memberUser && (
              <button
                className="mobile-nav-link mobile-user-action"
                onClick={() => { goLogin(); setShowMobileMenu(false); }}
              >
                <i className="fas fa-user" /> {isCostal ? 'Ingresar' : 'Acceso'}
              </button>
            )}
          </nav>
        )}
        
        <div className="header-actions">
          {memberUser ? (
            <div className="header-member-session desktop-only" aria-live="polite">
              <MemberAccountMenu
                member={memberUser}
                onNavigate={navigateToPage}
                onLogout={handleMemberLogout}
              />
            </div>
          ) : (
            <button
              type="button"
              className={`header-acceso-btn${isCostal ? ' header-acceso-btn--quiet' : ''}`}
              onClick={goLogin}
              title={isCostal ? 'Ingresar' : 'Acceso comunidad Javiland'}
              aria-label={isCostal ? 'Ingresar' : 'Acceso'}
            >
              <i className={`fas ${isCostal ? 'fa-user' : 'fa-user-circle'}`} aria-hidden />
              {!isCostal && <span className="header-acceso-label">Acceso</span>}
            </button>
          )}
          {isCostal && (
          <button
            type="button"
            className="cart-icon"
            onClick={toggleCart}
            title={t('header.cart')}
            aria-label={t('header.cart')}
          >
            <i className="fas fa-shopping-cart"></i>
            {itemCount > 0 && (
              <span className="cart-badge">{itemCount}</span>
            )}
          </button>
          )}
        </div>
      </div>
    </header>
    </>
  );
};

export default Header;
